import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Plus, Trash2, Pencil, Save, X, Wrench, Package } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", price: 0, point: 0, kind: "jasa" };

const CatalogManager = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("jasa");
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/catalog");
      setItems(r.data || []);
    } catch {
      toast.error("Gagal memuat katalog");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = (kind) => { setForm({ ...empty, kind }); setEditingId(null); };
  const startEdit = (it) => { setForm({ name: it.name, price: it.price, point: it.point || 0, kind: it.kind }); setEditingId(it.id); setTab(it.kind); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nama harus diisi"); return; }
    const payload = { name: form.name.trim(), price: Number(form.price) || 0, point: Number(form.point) || 0, kind: form.kind };
    try {
      if (editingId) await api.put(`/catalog/${editingId}`, payload);
      else await api.post("/catalog", payload);
      toast.success("Disimpan");
      setForm({ ...empty, kind: tab });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus "${it.name}"?`)) return;
    try {
      await api.delete(`/catalog/${it.id}`);
      toast.success("Dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const filtered = items.filter((x) => x.kind === tab);

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="catalog-manager">
      <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
        <Wrench size={14} />
        <div className="label-cap">Daftar Jasa & Material (Bonus Point)</div>
      </div>

      <div className="border-b border-[#E4E4E7] flex">
        <button onClick={() => { setTab("jasa"); startCreate("jasa"); }} data-testid="catalog-tab-jasa"
          className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${tab === "jasa" ? "bg-[#111113] text-white" : "hover:bg-[#F4F4F5]"}`}>
          <Wrench size={14} /> Jasa ({items.filter(x => x.kind === "jasa").length})
        </button>
        <button onClick={() => { setTab("material"); startCreate("material"); }} data-testid="catalog-tab-material"
          className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${tab === "material" ? "bg-[#111113] text-white" : "hover:bg-[#F4F4F5]"}`}>
          <Package size={14} /> Material ({items.filter(x => x.kind === "material").length})
        </button>
      </div>

      <form onSubmit={submit} className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-12 gap-3 border-b border-[#E4E4E7] bg-[#F4F4F5]">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder={tab === "jasa" ? "Nama Jasa (mis. Servis Pipa Bocor)" : "Nama Material (mis. Pipa PVC 1 inch)"}
          required
          data-testid="catalog-name-input"
          className="sm:col-span-5 px-3 py-2 border border-[#E4E4E7] text-sm bg-white"
        />
        <input
          type="number" value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
          placeholder="Harga"
          data-testid="catalog-price-input"
          className="sm:col-span-3 px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white"
        />
        {tab === "jasa" ? (
          <input
            type="number" value={form.point}
            onChange={(e) => setForm({ ...form, point: e.target.value })}
            placeholder="Point"
            title="Bonus point untuk teknisi (jasa saja)"
            data-testid="catalog-point-input"
            className="sm:col-span-2 px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white"
          />
        ) : (
          <div className="sm:col-span-2 text-xs text-[#52525B] flex items-center">(Material tidak ada point)</div>
        )}
        <button type="submit" data-testid="catalog-submit-button" className="sm:col-span-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase px-3 py-2 flex items-center justify-center gap-2">
          {editingId ? <><Save size={14} /> Update</> : <><Plus size={14} /> Tambah</>}
        </button>
        {editingId && (
          <button type="button" onClick={() => { setEditingId(null); setForm({ ...empty, kind: tab }); }} className="sm:col-span-12 text-xs text-[#52525B] underline text-left">Batal edit</button>
        )}
      </form>

      <div>
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-[#52525B] text-center">
            Belum ada {tab === "jasa" ? "jasa" : "material"}. Tambahkan dari form di atas — akan muncul sebagai pilihan saat tambah item di Order.
          </div>
        ) : (
          <ul className="divide-y divide-[#E4E4E7]">
            {filtered.map((it) => (
              <li key={it.id} className="flex items-center gap-3 px-4 sm:px-6 py-3" data-testid={`catalog-item-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{it.name}</div>
                  <div className="text-xs text-[#52525B] font-mono">
                    Rp {(it.price || 0).toLocaleString("id-ID")}
                    {it.kind === "jasa" && it.point > 0 && <span className="ml-2 text-[#7C3AED]">· {it.point} pts</span>}
                  </div>
                </div>
                <button onClick={() => startEdit(it)} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]" data-testid={`catalog-edit-${it.id}`}><Pencil size={14} /></button>
                <button onClick={() => remove(it)} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]" data-testid={`catalog-delete-${it.id}`}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CatalogManager;
